import { useCallback } from 'react';

import { APIConfiguration } from '@savgroup-front-common/configuration';
import { DOCUMENT_TYPES } from '@savgroup-front-common/constants';
import { isBackResponseFailure } from '@savgroup-front-common/types';

import {
  CommonAttachmentService,
  CommonCarrierService,
  CommonClaimService,
  CommonDocumentService,
} from '../api';
import setCarrierLabelDownloadedCommand from '../api/Carrier/setCarrierLabelDownloadedCommand';
import { toast } from '../atoms/Toast';

interface HandleActionDownloadDocumentArgs {
  downloadType: string;
  tokenId?: string;
  fileId?: string;
  moduleDefinitionId?: string;
  moduleInstanceId?: string;
  fileName: string;
  carrierLabelId?: string;
  partnerTransportDocumentUrl?: string;
}

interface HandleDownloadDocumentByUrlArgs {
  documentUrl?: string;
  fileName: string;
}

const resolveDocumentUrl = (documentUrl: string): string => {
  const isUrlReady =
    documentUrl.includes('https://') || documentUrl.includes('//localhost/');

  return isUrlReady ? documentUrl : `${APIConfiguration.base}${documentUrl}`;
};

const openExternalDocument = (documentUrl?: string): void => {
  if (!documentUrl) {
    return;
  }

  window.open(resolveDocumentUrl(documentUrl), '_blank', 'noopener,noreferrer');
};

const downloadFileWithUrl = ({
  data,
  name,
}: {
  data?: Blob;
  name: string;
}): void => {
  if (!data) {
    return;
  }

  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');

  document.body.appendChild(anchor);
  anchor.setAttribute('style', 'display: none');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  window.URL.revokeObjectURL(url);
  anchor.remove();
};

const useDownloadFileDocument = () => {
  const handleActionDownloadDocument = useCallback(
    async ({
      downloadType,
      tokenId,
      fileId,
      moduleDefinitionId,
      moduleInstanceId,
      fileName,
      carrierLabelId,
      partnerTransportDocumentUrl,
    }: HandleActionDownloadDocumentArgs) => {
      let response;

      if (downloadType === DOCUMENT_TYPES.SHIPPING_LABEL) {
        if (partnerTransportDocumentUrl) {
          openExternalDocument(partnerTransportDocumentUrl);

          if (carrierLabelId) {
            await setCarrierLabelDownloadedCommand({
              carrierLabelId,
              downloaded: true,
            });
          }

          return undefined;
        }

        if (!moduleDefinitionId || !moduleInstanceId || !fileId) {
          return undefined;
        }

        const responseUrl = await CommonCarrierService.getCarrierLabelUrlQuery({
          fileId,
          moduleDefinitionId,
          moduleInstanceId,
        });

        if (isBackResponseFailure(responseUrl)) {
          toast.error(responseUrl.errors.at(0)?.message);

          return undefined;
        }

        const shippingLabelTokenId = responseUrl.value?.split('token=')[1];

        if (!shippingLabelTokenId) {
          return undefined;
        }

        response = await CommonCarrierService.getCarrierLabelDocument({
          fileId,
          tokenId: shippingLabelTokenId,
        });
      } else if (downloadType === DOCUMENT_TYPES.RETURN_VOUCHER) {
        if (!tokenId) {
          return undefined;
        }

        response = await CommonDocumentService.downloadDocument({
          tokenId,
        });
      } else if (
        downloadType === DOCUMENT_TYPES.ADDITIONAL_INFORMATION_DOCUMENTS
      ) {
        if (!tokenId) {
          return undefined;
        }

        response =
          await CommonDocumentService.downloadDocumentAdditionalInformation({
            tokenId,
          });
      } else {
        if (!tokenId || !fileName) {
          return undefined;
        }

        response = await CommonClaimService.getClaimDocument({
          fileName,
          tokenId,
        });
      }

      if (isBackResponseFailure(response)) {
        toast.error(response.errors.at(0)?.message);

        return undefined;
      }

      downloadFileWithUrl({ data: response, name: fileName });

      if (downloadType === DOCUMENT_TYPES.SHIPPING_LABEL && carrierLabelId) {
        await setCarrierLabelDownloadedCommand({
          carrierLabelId,
          downloaded: true,
        });
      }

      return response;
    },
    [],
  );

  const handleDownloadDocumentByUrl = useCallback(
    async ({ documentUrl, fileName }: HandleDownloadDocumentByUrlArgs) => {
      if (!documentUrl) {
        return undefined;
      }

      const response = await CommonAttachmentService.downloadAttachment({
        endpoint: resolveDocumentUrl(documentUrl),
      });

      if (isBackResponseFailure(response)) {
        toast.error(response.errors.at(0)?.message);

        return undefined;
      }

      downloadFileWithUrl({ data: response, name: fileName });

      return response;
    },
    [],
  );

  return { handleActionDownloadDocument, handleDownloadDocumentByUrl };
};

export default useDownloadFileDocument;
